import React, { Suspense, lazy } from 'react';
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import './App.css';

// Context
import { AuthProvider } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { LanguageProvider } from './contexts/LanguageContext';

// Components
import Layout from './components/Layout';
import PublicLayout from './components/PublicLayout';
import ProtectedRoute from './components/ProtectedRoute';
import DatabaseInitializer from './components/DatabaseInitializer';
import ErrorBoundary from './components/ErrorBoundary';

// Loading component
const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="text-center">
      <div className="inline-block animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-gold-500 mb-4"></div>
      <div className="text-xl font-semibold text-gray-700">Chargement...</div>
    </div>
  </div>
);

// Pages publiques - chargées immédiatement
import Landing from './pages/Landing';
import Login from './pages/Login';
import AccessDenied from './pages/AccessDenied';
import CGV from './pages/CGV';
import CGU from './pages/CGU';
import PolitiqueConfidentialite from './pages/PolitiqueConfidentialite';
// TransactionForm chargé directement pour éviter les problèmes de chunk
import TransactionForm from './pages/TransactionForm';

// Fonction helper pour le lazy loading avec gestion d'erreur
const lazyLoad = (importFunc) => {
  return lazy(() => 
    importFunc().catch((error) => {
      console.error('Erreur chargement composant:', error);
      // Retourner un composant d'erreur par défaut
      return {
        default: () => (
          <div className="min-h-screen flex items-center justify-center bg-gray-50">
            <div className="text-center">
              <div className="text-red-600 text-xl mb-4">Erreur de chargement</div>
              <button 
                onClick={() => window.location.reload()} 
                className="px-4 py-2 bg-navy-600 text-white rounded-lg hover:bg-navy-700"
              >
                Recharger la page
              </button>
            </div>
          </div>
        )
      };
    })
  );
};

// Pages protégées - lazy loading pour optimiser le bundle
const Home = lazyLoad(() => import('./pages/Home'));
const Dashboard = lazyLoad(() => import('./pages/Dashboard'));
const TransactionsList = lazyLoad(() => import('./pages/TransactionsList'));
const TransactionDossier = lazyLoad(() => import('./pages/TransactionDossier'));
const Positions = lazyLoad(() => import('./pages/Positions'));
const Stock = lazyLoad(() => import('./pages/Stock'));
const Margins = lazyLoad(() => import('./pages/Margins'));
const Admin = lazyLoad(() => import('./pages/Admin'));
const AlertSettings = lazyLoad(() => import('./pages/AlertSettings'));
const Profile = lazyLoad(() => import('./pages/Profile'));
const Reports = lazyLoad(() => import('./pages/Reports'));
const FinancialFlows = lazyLoad(() => import('./pages/FinancialFlows'));
const FinancialFlowsDashboard = lazyLoad(() => import('./pages/FinancialFlowsDashboard'));
const SwiftPayments = lazyLoad(() => import('./pages/SwiftPayments'));
const Barres = lazyLoad(() => import('./pages/Barres'));
const Factures = lazyLoad(() => import('./pages/Factures'));
const Raffinage = lazyLoad(() => import('./pages/Raffinage'));
const ModuleExtensions = lazyLoad(() => import('./pages/ModuleExtensions'));
const ComptabiliteMetier = lazyLoad(() => import('./pages/ComptabiliteMetier'));
const ParametresGeneraux = lazyLoad(() => import('./pages/ParametresGeneraux'));
const ArchivesDocuments = lazyLoad(() => import('./pages/ArchivesDocuments'));

function App() {
  console.log('🚀 App component rendering...');
  
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <LanguageProvider>
          <DatabaseInitializer>
            <AuthProvider>
              <NotificationProvider>
                <Router>
                  <Routes>
            {/* Page publique de présentation - accessible sans authentification */}
            <Route path="/" element={<Landing />} />
            
            {/* Route Login - accessible sans authentification */}
            <Route path="/login" element={<Login />} />
            
            {/* Page d'accès refusé - accessible sans authentification */}
            <Route path="/access-denied" element={<AccessDenied />} />
            
            {/* Pages légales - accessibles sans authentification avec PublicLayout */}
            <Route path="/cgv" element={<PublicLayout><CGV /></PublicLayout>} />
            <Route path="/cgu" element={<PublicLayout><CGU /></PublicLayout>} />
            <Route path="/politique-confidentialite" element={<PublicLayout><PolitiqueConfidentialite /></PublicLayout>} />
            
            {/* TOUTES les autres routes sont protégées - nécessitent une authentification */}
            <Route path="/*" element={
              <ProtectedRoute>
                <Layout>
                  <ErrorBoundary>
                    <Suspense fallback={<LoadingSpinner />}>
                      <Routes>
                      {/* Page d'accueil authentifiée */}
                      <Route path="/home" element={<Home />} />
                      
                      {/* Pages protégées - lazy loaded */}
                      <Route path="/dashboard" element={<Dashboard />} />
                      <Route path="/transaction/new" element={<TransactionForm />} />
                      <Route path="/transactions" element={<TransactionsList />} />
                      <Route path="/transactions/:id/dossier" element={<TransactionDossier />} />
                      <Route path="/positions" element={<Positions />} />
                      <Route path="/stock" element={<Stock />} />
                      <Route path="/margins" element={<Margins />} />
                      
                      {/* Page Admin - réservée à l'administrateur principal */}
                      <Route path="/admin" element={<Admin />} />
                      
                      {/* Paramètres d'alertes */}
                      <Route path="/alert-settings" element={<AlertSettings />} />
                      
                      {/* Profil utilisateur */}
                      <Route path="/profile" element={<Profile />} />
                      
                      {/* Rapports et Analytics */}
                      <Route path="/reports" element={<Reports />} />
                      
                      {/* Flux Financiers Multi-Partenaires */}
                      <Route path="/financial-flows" element={<FinancialFlows />} />
                      <Route path="/financial-flows/dashboard" element={<FinancialFlowsDashboard />} />
                      
                      {/* Paiements SWIFT */}
                      <Route path="/swift" element={<SwiftPayments />} />
                      
                      {/* CDC client : Barres, Factures, Raffinage */}
                      <Route path="/barres" element={<Barres />} />
                      <Route path="/factures" element={<Factures />} />
                      <Route path="/raffinage" element={<Raffinage />} />
                      <Route path="/extensions" element={<ModuleExtensions />} />
                      <Route path="/comptabilite" element={<ComptabiliteMetier />} />
                      <Route path="/parametres" element={<ParametresGeneraux />} />
                      <Route path="/archives-documents" element={<ArchivesDocuments />} />
                      
                      {/* Redirection pour routes inconnues */}
                      <Route path="*" element={<Navigate to="/" replace />} />
                      </Routes>
                    </Suspense>
                  </ErrorBoundary>
                </Layout>
              </ProtectedRoute>
            } />
          </Routes>
                </Router>
              </NotificationProvider>
            </AuthProvider>
          </DatabaseInitializer>
        </LanguageProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
